import _ from "lodash";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FlashMessage } from "../../../components/common";
import notificationServices from "../../../services/notification.services";
import searchAndConnectServices from "../../../services/searchAndConnect.services";
import { Menu, MenuItem, MenuButton } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import appRoutes from "../../../routes/app.routes";
import Avatar from "../../../components/common/AvtarImage";
import { convertUTCToLocal } from "../../../utils/helper";
import { rootReducersState } from "../../../reducers";

const NOTIFICATION_TYPE = {
  APPLICATION_PROCESS: 1,
  CONNECTION: 2,
  CANDIDATE_JOB_APPLIED: 3,
  JOB_EXPIRED: 4,
  COMPANY_JOB_POSTED: 12,
  COMPANY_POST_PUBLISHED: 13,
};

interface NotificationItemProps {
  notification: any | null;
  pendingConnectionId?: number;
  handleClear: React.Dispatch<React.SetStateAction<string>>;
  handleRead: React.Dispatch<React.SetStateAction<string>>;
  onConnectionResolved?: (userId: number) => void;
  setViewRecommendationModal: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentRecommendation: React.Dispatch<React.SetStateAction<any>>;
}

const NotificationItem = ({
  notification,
  pendingConnectionId,
  handleClear,
  handleRead,
  onConnectionResolved,
  setViewRecommendationModal,
  setCurrentRecommendation,
}: NotificationItemProps) => {
  const [actionLoading, setActionLoading] = useState(false);
  const [actionTaken, setActionTaken] = useState(false);
  const currentCompany = useSelector(
    ({ session }: rootReducersState) => session.currentCompany
  );

  const isUnread = _.get(notification, "is_read") === 1;
  const payloadText = String(_.get(notification, "payload", "") || "");
  const typeId = Number(_.get(notification, "notification_type_id", 0));
  const connectedUserId = _.get(notification, "connected_user_id");
  const userSlug = _.get(notification, "user.slug", "");
  const postSlug = _.get(notification, "post.slug", "");
  const jobSlug = _.get(notification, "job.slug", "");
  const firstName = _.get(notification, "user.first_name", "");
  const lastName = _.get(notification, "user.last_name", "");
  const displayName = `${firstName} ${lastName}`.trim();
  const companyLogo =
    _.get(notification, "job.companyProfile.company_logo") ||
    _.get(notification, "post.companyProfile.company_logo") ||
    "";
  const avatarSrc =
    companyLogo ||
    _.get(notification, "user.profile_image") ||
    _.get(currentCompany, "company_logo") ||
    "";
  const isCompanyAvatar =
    !!companyLogo ||
    (!_.get(notification, "user.profile_image") &&
      !!_.get(currentCompany, "company_logo"));
  const timestamp = convertUTCToLocal(
    _.get(notification, "created_at", "")
  ).trim();
  const isConnectionRequest =
    typeId === NOTIFICATION_TYPE.CONNECTION &&
    /connection request/i.test(payloadText);
  const isRecommendation = !!_.get(notification, "recommendation_id");
  const isJobApplied = typeId === NOTIFICATION_TYPE.CANDIDATE_JOB_APPLIED;
  const isJobExpired = typeId === NOTIFICATION_TYPE.JOB_EXPIRED;
  const isApplicationUpdate =
    typeId === NOTIFICATION_TYPE.APPLICATION_PROCESS;
  const isCompanyJobPosted = typeId === NOTIFICATION_TYPE.COMPANY_JOB_POSTED;
  const isCompanyPostPublished =
    typeId === NOTIFICATION_TYPE.COMPANY_POST_PUBLISHED;

  const formattedPayload = useMemo(() => {
    if (!payloadText) return payloadText;
    if (displayName && payloadText.includes(displayName)) {
      const parts = payloadText.split(displayName);
      return (
        <>
          {parts.map((part, index) => (
            <React.Fragment key={`${part}-${index}`}>
              {index > 0 && <strong>{displayName}</strong>}
              {part}
            </React.Fragment>
          ))}
        </>
      );
    }
    return payloadText;
  }, [payloadText, displayName]);

  const _handleClear = async (id) => {
    try {
      const result = await notificationServices.clearNotification(id);

      if (result.flag) {
        handleClear(id);
      } else {
        FlashMessage(result.message, "error");
      }
    } catch (error) {
      FlashMessage(error?.message, "error");
    }
  };

  const _handleRead = async (id) => {
    try {
      const result = await notificationServices.readNotification(id);

      if (result.flag) {
        handleRead(id);
      } else {
        FlashMessage(result.message, "error");
      }
    } catch (error) {
      FlashMessage(error?.message, "error");
    }
  };

  const openRecommendation = () => {
    setCurrentRecommendation(notification);
    setViewRecommendationModal(true);
    if (isUnread) {
      _handleRead(_.get(notification, "id"));
    }
  };

  const handleOpenDetails = () => {
    if (isRecommendation) {
      openRecommendation();
      return;
    }

    if (!_.isEmpty(postSlug)) {
      _handleRead(notification.id);
      const postLink = appRoutes.FeedDetails.generateFullPath(postSlug);
      window.open(postLink, "_blank");
      return;
    }

    if (!_.isEmpty(jobSlug)) {
      _handleRead(notification.id);
      window.open(appRoutes.jobDetail.generatePath(jobSlug), "_blank");
      return;
    }

    if (!_.isEmpty(userSlug)) {
      _handleRead(notification.id);
      const connectionLink =
        appRoutes.candidatePublicProfile.generateFullPath(userSlug);
      window.open(connectionLink, "_blank");
      return;
    }

    _handleRead(notification.id);
  };

  const findPendingConnection = async () => {
    if (pendingConnectionId) {
      return { id: pendingConnectionId };
    }
    const result = await searchAndConnectServices.getConnectionRequests({
      type: "received",
      page: 1,
      pageSize: 50,
    });
    const list = _.get(result, "data.data", []);
    return (list || []).find(
      (item) => Number(_.get(item, "requester.id")) === Number(connectedUserId)
    );
  };

  const handleAccept = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const pending = await findPendingConnection();
      if (pending?.id) {
        const result = await searchAndConnectServices.updateCandidateConnection({
          id: pending.id,
          type: "accepted",
          reject_reason: "",
        });
        if (result.flag) {
          FlashMessage(result.message || "Connection accepted");
          setActionTaken(true);
          onConnectionResolved?.(Number(connectedUserId));
          if (isUnread) _handleRead(_.get(notification, "id"));
        } else {
          FlashMessage(result.message, "error");
        }
      } else {
        setActionTaken(true);
        onConnectionResolved?.(Number(connectedUserId));
      }
    } catch (error) {
      FlashMessage(error?.message || "Something went wrong!", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const pending = await findPendingConnection();
      if (pending?.id) {
        const result = await searchAndConnectServices.updateCandidateConnection({
          id: pending.id,
          type: "rejected",
          reject_reason: "",
        });
        if (result.flag) {
          FlashMessage(result.message || "Connection declined");
          setActionTaken(true);
          onConnectionResolved?.(Number(connectedUserId));
          if (isUnread) _handleRead(_.get(notification, "id"));
        } else {
          FlashMessage(result.message, "error");
        }
      } else {
        setActionTaken(true);
        onConnectionResolved?.(Number(connectedUserId));
      }
    } catch (error) {
      FlashMessage(error?.message || "Something went wrong!", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewJob = (event: React.MouseEvent) => {
    event.stopPropagation();
    const companySlug = _.get(currentCompany, "slug", "");
    if (isUnread) _handleRead(_.get(notification, "id"));

    if (isJobApplied && companySlug) {
      window.location.href = appRoutes.companyCandidates.generatePath(companySlug);
      return;
    }

    if (isJobExpired && companySlug) {
      window.location.href = appRoutes.companyJobs.generatePath(companySlug);
      return;
    }

    if (isApplicationUpdate) {
      window.location.href = appRoutes.candidateJobHub.generatePath("applied");
      return;
    }

    if (isCompanyJobPosted && jobSlug) {
      window.open(appRoutes.jobDetail.generatePath(jobSlug), "_blank");
      return;
    }

    if (isCompanyPostPublished && postSlug) {
      window.open(appRoutes.FeedDetails.generateFullPath(postSlug), "_blank");
      return;
    }

    if (!_.isEmpty(userSlug)) {
      window.open(
        appRoutes.candidatePublicProfile.generateFullPath(userSlug),
        "_blank"
      );
    }
  };

  const showConnectionActions =
    isConnectionRequest && !actionTaken && !!pendingConnectionId;
  const showJobAction =
    (isJobApplied ||
      isJobExpired ||
      isApplicationUpdate ||
      isCompanyJobPosted ||
      isCompanyPostPublished) &&
    !actionTaken;

  return (
    <div
      className={`notification-item ${isUnread ? "is-unread" : "is-read"}`}
      onClick={handleOpenDetails}
    >
      <div className="notification-item-indicator">
        {isUnread && <span className="notification-dot" />}
      </div>
      <div className="notification-item-avatar">
        <Avatar
          src={avatarSrc}
          type={isCompanyAvatar ? "company" : "candidate"}
          className="notification-avatar"
        />
      </div>
      <div className="notification-item-content">
        <p className="notification-item-text">{formattedPayload}</p>
        {(showConnectionActions || showJobAction) && (
          <div
            className="notification-item-actions"
            onClick={(event) => event.stopPropagation()}
          >
            {showConnectionActions && (
              <>
                <button
                  type="button"
                  className="notification-btn primary"
                  disabled={actionLoading}
                  onClick={handleAccept}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="notification-btn secondary"
                  disabled={actionLoading}
                  onClick={handleDecline}
                >
                  Decline
                </button>
              </>
            )}
            {showJobAction && (
              <button
                type="button"
                className="notification-btn primary"
                onClick={handleViewJob}
              >
                {isJobApplied
                  ? "View Candidate"
                  : isCompanyPostPublished
                    ? "View Post"
                    : "View Job"}
              </button>
            )}
          </div>
        )}
      </div>
      <div
        className="notification-item-meta"
        onClick={(event) => event.stopPropagation()}
      >
        {timestamp && <span className="notification-item-time">{timestamp}</span>}
        <Menu
          menuButton={
            <MenuButton className="notification-menu-button">
              <MoreHorizIcon />
            </MenuButton>
          }
          menuClassName="notification-dropdown-menu"
          portal
          align="end"
          direction="bottom"
          gap={8}
          transition
        >
          {isUnread && (
            <MenuItem
              onClick={() => {
                _handleRead(_.get(notification, "id"));
              }}
            >
              Mark as read
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              _handleClear(_.get(notification, "id"));
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default NotificationItem;
